import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAgentRoles } from "../agentRoles/useAgentRoles";
import type { ToolAllowlistItem } from "./api";
import { isRoleCriticalTool } from "./footgun";
import { useAddToolAllowlistItem } from "./useAddToolAllowlistItem";
import { useRemoveToolAllowlistItem } from "./useRemoveToolAllowlistItem";
import { useToolAllowlist } from "./useToolAllowlist";

// Секция «Разрешённые инструменты роли» на `/settings/tool-allowlist`.
// Человек выбирает роль (coder/reviewer/...), видит список разрешённых
// инструментов per-(org, role), добавляет и удаляет их (source=manual).
// Зеркало `ToolBlacklistSection`: Select роли + footgun-предупреждение про
// роль-критичные инструменты (спека § «Footgun»), НО семантика инвертирована —
// опасно УБРАТЬ критичный инструмент из allow-list'а, поэтому footgun-Alert
// живёт в модалке удаления, а не в форме добавления.
//
// Управляющая часть (форма add + список + модалка delete) вынесена в
// `ToolAllowlistManager`, который принимает выбранную роль пропсом. Это
// отделяет хуки мутаций (зависят от роли) от Mantine Select, чей onChange в
// jsdom не зовётся — менеджер тестируется напрямую с заданной ролью.

// Цвет Badge источника: правка человека (manual) vs самообучение.
function sourceColor(source: ToolAllowlistItem["source"]): string {
  return source === "manual" ? "blue" : "grape";
}

// Что показать под Select'ом: управление разрешениями выбранной роли или
// подсказку «выберите роль». Вынесено чистой функцией — Mantine Select в
// jsdom нельзя выбрать (floating-ui требует browser-API), поэтому обе ветки
// тестируем здесь напрямую, а не через клик по выпадашке.
export function renderManagerOrHint(
  roleName: string | null,
  t: ReturnType<typeof useTranslation>["t"],
): ReactElement {
  if (roleName === null) {
    return <Text c="dimmed">{t("toolAllowlist.selectRole")}</Text>;
  }
  // key по роли: смена роли ремаунтит менеджер, обнуляя черновик нового
  // инструмента и открытую модалку удаления прошлой роли.
  return <ToolAllowlistManager key={roleName} roleName={roleName} />;
}

export function ToolAllowlistSection(): ReactElement {
  const { t } = useTranslation();
  const roles = useAgentRoles();
  const [roleName, setRoleName] = useState<string | null>(null);

  const roleOptions = (roles.data ?? []).map((role) => ({
    value: role.role_name,
    label: t(`agentRoles.role.${role.role_name}`, {
      defaultValue: role.role_name,
    }),
  }));

  return (
    <Stack gap="md">
      <Title order={2}>{t("toolAllowlist.title")}</Title>
      <Text c="dimmed">{t("toolAllowlist.intro")}</Text>

      <Select
        label={t("toolAllowlist.roleLabel")}
        description={t("toolAllowlist.roleHint")}
        placeholder={t("toolAllowlist.rolePlaceholder")}
        data={roleOptions}
        value={roleName}
        onChange={setRoleName}
        disabled={roles.isLoading}
        error={roles.isError ? t("toolAllowlist.rolesError") : undefined}
      />

      {renderManagerOrHint(roleName, t)}
    </Stack>
  );
}

// Управление разрешениями выбранной роли: форма add, список и модалка
// подтверждения delete (с footgun-Alert при снятии роль-критичного
// инструмента). Роль — проп (Select обойдён); хуки мутаций привязаны к ней.
export function ToolAllowlistManager({
  roleName,
}: {
  readonly roleName: string;
}): ReactElement {
  const { t } = useTranslation();
  const [newTool, setNewTool] = useState("");
  const [deleting, setDeleting] = useState<ToolAllowlistItem | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const allowlist = useToolAllowlist(roleName);
  const addMutation = useAddToolAllowlistItem();
  const removeMutation = useRemoveToolAllowlistItem();

  const trimmedTool = newTool.trim();
  const canAdd = trimmedTool.length > 0;
  const deleteFootgun =
    deleting !== null &&
    isRoleCriticalTool(deleting.role_name, deleting.tool_name);

  const cancelDelete = (): void => {
    setDeleting(null);
    setRemoveError(null);
  };

  const handleAdd = (): void => {
    setAddError(null);
    addMutation.mutate(
      { role_name: roleName, tool_name: trimmedTool },
      {
        onSuccess: () => {
          setNewTool("");
        },
        onError: (error) => {
          setAddError(t("toolAllowlist.addError", { message: error.message }));
        },
      },
    );
  };

  return (
    <Stack gap="md">
      {addError !== null ? (
        <Alert color="red" title={t("toolAllowlist.errorHeading")}>
          {addError}
        </Alert>
      ) : null}

      <AllowlistBody
        allowlist={allowlist}
        newTool={newTool}
        setNewTool={setNewTool}
        canAdd={canAdd}
        adding={addMutation.isPending}
        onAdd={handleAdd}
        onDelete={(item) => {
          setRemoveError(null);
          setDeleting(item);
        }}
        t={t}
      />

      <Modal
        opened={deleting !== null}
        onClose={cancelDelete}
        title={t("toolAllowlist.delete.confirmTitle")}
        centered
      >
        {deleting !== null ? (
          <Stack gap="md">
            <Text>
              {t("toolAllowlist.delete.confirmText", {
                tool: deleting.tool_name,
              })}
            </Text>
            {deleteFootgun ? (
              <Alert color="orange" title={t("toolAllowlist.footgun.title")}>
                {t("toolAllowlist.footgun.text")}
              </Alert>
            ) : null}
            {removeError !== null ? (
              <Alert color="red" title={t("toolAllowlist.errorHeading")}>
                {removeError}
              </Alert>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={cancelDelete}>
                {t("toolAllowlist.modalCancel")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  removeMutation.mutate(
                    {
                      roleName: deleting.role_name,
                      toolName: deleting.tool_name,
                    },
                    {
                      onSuccess: () => {
                        setDeleting(null);
                      },
                      onError: (error) => {
                        setRemoveError(
                          t("toolAllowlist.removeError", {
                            message: error.message,
                          }),
                        );
                      },
                    },
                  );
                }}
                loading={removeMutation.isPending}
              >
                {t("toolAllowlist.delete.confirmButton")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

// Тело секции: форма добавления и список разрешений.
// Loader/Error/empty/таблица — взаимоисключающие состояния списка.
export function AllowlistBody({
  allowlist,
  newTool,
  setNewTool,
  canAdd,
  adding,
  onAdd,
  onDelete,
  t,
}: {
  readonly allowlist: ReturnType<typeof useToolAllowlist>;
  readonly newTool: string;
  readonly setNewTool: (value: string) => void;
  readonly canAdd: boolean;
  readonly adding: boolean;
  readonly onAdd: () => void;
  readonly onDelete: (item: ToolAllowlistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm">
        <TextInput
          style={{ flex: 1 }}
          label={t("toolAllowlist.newToolLabel")}
          placeholder={t("toolAllowlist.newToolPlaceholder")}
          value={newTool}
          onChange={(event) => {
            setNewTool(event.currentTarget.value);
          }}
        />
        <Button onClick={onAdd} disabled={!canAdd} loading={adding}>
          {t("toolAllowlist.addButton")}
        </Button>
      </Group>

      <AllowlistList allowlist={allowlist} onDelete={onDelete} t={t} />
    </Stack>
  );
}

// Список разрешений: один из четырёх взаимоисключающих результатов запроса.
function AllowlistList({
  allowlist,
  onDelete,
  t,
}: {
  readonly allowlist: ReturnType<typeof useToolAllowlist>;
  readonly onDelete: (item: ToolAllowlistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  if (allowlist.isPending) {
    return <Loader />;
  }
  if (allowlist.isError) {
    return (
      <Alert color="red" title={t("toolAllowlist.errorHeading")}>
        {t("toolAllowlist.loadingError")}
      </Alert>
    );
  }
  if (allowlist.data.length === 0) {
    return <Text c="dimmed">{t("toolAllowlist.empty")}</Text>;
  }
  return (
    <Table withTableBorder withColumnBorders striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("toolAllowlist.colTool")}</Table.Th>
          <Table.Th>{t("toolAllowlist.colSource")}</Table.Th>
          <Table.Th>{t("toolAllowlist.colManage")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {allowlist.data.map((item) => (
          <Table.Tr key={item.tool_name}>
            <Table.Td>
              <Group gap="xs">
                <Text>{item.tool_name}</Text>
                {item.known ? null : (
                  <Badge color="orange" variant="light">
                    {t("toolAllowlist.unknownTool")}
                  </Badge>
                )}
              </Group>
            </Table.Td>
            <Table.Td>
              <Badge variant="light" color={sourceColor(item.source)}>
                {t(`toolAllowlist.source.${item.source}`)}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Button
                size="xs"
                color="red"
                variant="subtle"
                onClick={() => {
                  onDelete(item);
                }}
              >
                {t("toolAllowlist.deleteButton")}
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
